"use client";

import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { CopyButton } from "@/app/components/ui/copy-button";
import { CheckCircle, Key, Download, Info } from "lucide-react";
import { validate as uuidValidate, version as getUUIDVersionFromString } from "uuid";
import { UUIDGeneratorState } from "./types";

interface UUIDResultsProps {
  state: UUIDGeneratorState;
  onDownload: () => void;
}

export default function UUIDResults({
  state,
  onDownload,
}: UUIDResultsProps) {
  // UUID validation helper
  const validateUUID = (uuid: string): boolean => {
    return uuidValidate(uuid);
  };

  // Get UUID version
  const getUUIDVersion = (uuid: string): number => {
    return getUUIDVersionFromString(uuid);
  };

  return (
    <Card className="card-enhanced">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          <span>Generated UUIDs</span>
          {state.generatedUUIDs.length > 0 && (
            <span className="text-sm text-muted-foreground">
              ({state.generatedUUIDs.length} generated)
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Your generated UUIDs will appear here
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.generatedUUIDs.length > 0 && (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Version: {state.uuidVersion.toUpperCase()}
              </span>
              {state.generatedUUIDs.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="h-3 w-3" />
                  <span>Valid: {state.generatedUUIDs.filter(validateUUID).length}/{state.generatedUUIDs.length}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <CopyButton
                text={state.generatedUUIDs.join('\n')}
                variant="outline"
                size="sm"
                className="hover-lift"
              >
                Copy All
              </CopyButton>
              <Button
                onClick={onDownload}
                variant="outline"
                size="sm"
                className="hover-lift"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {state.generatedUUIDs.length > 0 ? (
            state.generatedUUIDs.map((uuid, index) => {
              const isValid = validateUUID(uuid);
              const detectedVersion = isValid ? getUUIDVersion(uuid) : null;
              
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <code className="font-mono text-sm break-all">{uuid}</code>
                    {isValid && detectedVersion !== null && (
                      <div className="flex items-center gap-2 mt-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="text-xs text-muted-foreground">
                          Valid UUID v{detectedVersion}
                        </span>
                      </div>
                    )}
                  </div>
                  <CopyButton
                    text={uuid}
                    variant="ghost"
                    size="sm"
                    className="ml-2 shrink-0"
                  />
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Generated UUIDs will appear here</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
